/*
 * MinesGesture — ONE owner for every gesture on the minesweeper board.
 *
 * A cell takes four different intentions from the same finger or mouse —
 * open, flag, chord, and "I am only moving the board" — and a mistake between
 * them ends the game. So nothing on the board acts on a raw event: the page
 * feeds every pointer event here, and this module alone decides.
 *
 * ── Rules ────────────────────────────────────────────────────────────────
 *   finger / pen
 *     tap            → primary(cell). The verdict "this was a tap" is the
 *                      browser's `click`: it comes only if the finger stayed
 *                      in place and no scroll took the gesture. On top of
 *                      that a finger that travelled more than SLOP_PX is not
 *                      a tap even where there was nothing to scroll.
 *     long press     → secondary(cell) the moment the hold completes, while
 *                      the finger is still down. A ring grows under the
 *                      finger from RING_DELAY on; the timer and the system
 *                      `contextmenu` (Android sends one) are the same gesture:
 *                      whoever comes first acts, the other is silent, and the
 *                      `click` that follows a long press (iOS) is swallowed.
 *     abandoned hold → nothing. A finger lifted after the ring has visibly
 *                      started but before it completed neither opens nor
 *                      flags: "held almost long enough" must not open a mine.
 *     two fingers    → nothing on the cells (the page zooms the board).
 *   mouse
 *     left           → acts on RELEASE, on the cell under the pointer then
 *                      (press on one cell, slide, release on another — as in
 *                      every desktop minesweeper since 1990).
 *     right          → secondary(cell) on PRESS.
 *     middle, or left + right together → chord(cell) on release.
 *     The browser's `click` of a mouse gesture is ignored: the release has
 *     already acted.
 *   keyboard / assistive technology
 *     a `click` with detail 0 → primary(cell).
 *
 * The module knows neither the DOM nor the rules of the game: a cell is a
 * number, actions go back through the handlers. The same rules are checked in
 * node (tests/js/minesweeper-gesture.test.mjs) and on a real page with real
 * touch input. Why — docs/why/minesweeper.md and docs/why/one-gesture-one-owner.md.
 */
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.MinesGesture = factory();
})(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    var HOLD_MS = 350;            // long press; the page may set 250 / 350 / 500
    var RING_DELAY_MS = 110;      // a quick tap never shows the ring
    var TAP_SHARE = 0.7;          // a press longer than this share of the hold is no longer a tap
    var SLOP_PX = 10;             // a finger that went further is moving the board, not pressing
    var CLICK_AFTER_UP_MS = 700;  // a `click` later than this belongs to nobody's gesture

    function create(h) {
        var setTimer = h.setTimer || function (fn, ms) { return setTimeout(fn, ms); };
        var clearTimer = h.clearTimer || function (id) { clearTimeout(id); };
        var holdMs = h.holdMs || HOLD_MS;
        var slopPx = h.slopPx || SLOP_PX;
        var g = null;                 // the finger gesture in progress (or just ended)
        var fingers = 0;              // touch pointers currently down
        var mouse = null;             // {left, right, middle, chord, done}
        var rightAt = -1e9;           // when the right button last went down or up
        // `noclick` — a still finger lifted, and the browser sent no click: the
        // tap that stops a gliding board. The browser's rule, kept on purpose (the
        // cell under the finger is not the one the eye chose); counted, so the
        // field can tell how often a tap is spent that way.
        var counts = { taps: 0, holds: 0, abandoned: 0, drifted: 0, scrolled: 0, multi: 0, noclick: 0, wobble: 0 };
        var dirty = false;
        function count(k) { counts[k]++; dirty = true; }

        function stopTimers() {
            if (!g) return;
            if (g.holdTimer) { clearTimer(g.holdTimer); g.holdTimer = 0; }
            if (g.ringTimer) { clearTimer(g.ringTimer); g.ringTimer = 0; }
        }
        function endRing(fired) {
            if (g && g.ring) { g.ring = false; if (h.holdEnd) h.holdEnd(g.cell, !!fired); }
        }
        function fireHold() {
            if (!g || g.ended || g.moved || g.fired || g.multi) return;
            stopTimers();
            g.fired = true;
            count('holds');
            // The ring may not have started yet when the system menu came first.
            endRing(true);
            h.secondary(g.cell, 'hold');
        }
        function abandon() {
            stopTimers();
            endRing(false);
        }

        // ── Mouse ────────────────────────────────────────────────────────────
        // Which buttons are down is read from `buttons` on EVERY event, not from
        // the kind of event: a second button pressed while the first is held
        // arrives as a pointerMOVE (the Pointer Events rule for chorded
        // buttons), and so does the release of any button but the last.
        var BTN = [['left', 1, 0], ['right', 2, 2], ['middle', 4, 1]];
        function mousePress(name, e, cell) {
            if (!mouse) mouse = { left: false, right: false, middle: false, chord: false, done: false };
            mouse[name] = true;
            if (name === 'right') {
                rightAt = e.timeStamp;
                if (mouse.left) { mouse.chord = true; mouse.done = false; }
                else if (cell >= 0) h.secondary(cell, 'mouse');
            } else if (name === 'left' && mouse.right) { mouse.chord = true; mouse.done = false; }
        }
        function mouseRelease(name, e, cell) {
            var act = null;
            mouse[name] = false;
            if (name === 'right') rightAt = e.timeStamp;
            if (name === 'middle') act = 'chord';
            else if (mouse.chord) {
                if (!mouse.done) { act = 'chord'; mouse.done = true; }
                if (!mouse.left && !mouse.right) mouse.chord = false;
            } else if (name === 'left') act = 'primary';
            return act;
        }
        function reconcile(e, cell, kind) {
            var mask = typeof e.buttons === 'number' ? e.buttons : -1, i, name, act = null;
            if (mask < 0) {
                // No `buttons` on the event: rebuild it from what is tracked.
                mask = 0;
                for (i = 0; i < 3; i++) if (mouse && mouse[BTN[i][0]]) mask |= BTN[i][1];
            }
            for (i = 0; i < 3; i++) {
                if (e.button !== BTN[i][2]) continue;
                if (kind === 'down') mask |= BTN[i][1]; else if (kind === 'up') mask &= ~BTN[i][1];
            }
            for (i = 0; i < 3; i++) {
                name = BTN[i][0];
                if ((mask & BTN[i][1]) && !(mouse && mouse[name])) mousePress(name, e, cell);
            }
            for (i = 0; i < 3; i++) {
                name = BTN[i][0];
                if (!(mask & BTN[i][1]) && mouse && mouse[name]) act = mouseRelease(name, e, cell) || act;
            }
            if (!mouse) return;
            var held = mouse.left || mouse.right || mouse.middle;
            if (h.press) {
                if ((mouse.chord && !mouse.done) || mouse.middle) h.press(cell, 'chord');
                else if (mouse.left && !mouse.chord) h.press(cell, 'single');
                else h.press(-1, 'none');
            }
            if (!held) mouse = null;
            if (act === 'chord' && cell >= 0) h.chord(cell, 'mouse');
            else if (act === 'primary' && cell >= 0) h.primary(cell, 'mouse');
        }

        return {
            setHoldMs: function (ms) { holdMs = ms > 0 ? ms : HOLD_MS; },
            holdMs: function () { return holdMs; },

            /** pointerdown on the board; cell — index under the pointer, −1 when none. */
            down: function (e, cell) {
                if (e.pointerType === 'mouse' || (e.pointerType === 'pen' && (e.button === 2 || (e.buttons & 2)))) {
                    reconcile(e, cell, 'down');
                    return;
                }
                // A finger or a pen tip. The count of fingers is rebuilt from the
                // browser's own word on every first finger: a lost pointerup must
                // not leave the board believing in a second finger for ever.
                if (e.isPrimary === false) fingers = Math.max(2, fingers + 1);
                else fingers = 1;
                if (fingers > 1) {
                    // A second finger: the gesture belongs to the board's zoom now.
                    if (g && !g.ended) { g.multi = true; abandon(); count('multi'); }
                    return;
                }
                abandon();
                // The previous still tap got no click, and the next finger is already down.
                if (g && g.ended && !g.lost && !g.moved && !g.fired && !g.multi) count('noclick');
                g = {
                    id: e.pointerId, cell: cell, x: e.clientX, y: e.clientY, t: e.timeStamp,
                    moved: false, fired: false, multi: false, ended: false, upAt: 0, ring: false,
                    holdable: cell >= 0 && (!h.canHold || h.canHold(cell)),
                    holdTimer: 0, ringTimer: 0, wobble: 0, at: holdMs
                };
                if (g.holdable) {
                    g.holdTimer = setTimer(fireHold, holdMs);
                    g.ringTimer = setTimer(function () {
                        if (!g || g.ended || g.moved || g.fired || g.multi) return;
                        g.ringTimer = 0;
                        g.ring = true;
                        if (h.holdStart) h.holdStart(g.cell, holdMs - RING_DELAY_MS);
                    }, RING_DELAY_MS);
                }
            },

            /** pointermove anywhere; cell — index under the pointer. */
            move: function (e, cell) {
                if (e.pointerType === 'mouse' || (mouse && e.pointerType === 'pen')) {
                    reconcile(e, cell, 'move');
                    return;
                }
                if (!g || g.ended || e.pointerId !== g.id) return;
                var d = Math.hypot(e.clientX - g.x, e.clientY - g.y);
                if (d > g.wobble) g.wobble = d;
                if (!g.moved && d > slopPx) {
                    g.moved = true;
                    abandon();
                }
            },

            /** pointerup anywhere; cell — index under the pointer, −1 outside the board. */
            up: function (e, cell) {
                if (e.pointerType === 'mouse' || (mouse && e.pointerType === 'pen')) {
                    reconcile(e, cell, 'up');
                    return;
                }
                if (fingers > 0) fingers--;
                if (!g || g.ended || e.pointerId !== g.id) return;
                stopTimers();
                g.ended = true;
                g.upAt = e.timeStamp;
                g.held = e.timeStamp - g.t;
                endRing(false);
                var mine = g;
                if (!g.moved && !g.fired && !g.multi) setTimer(function () { if (g === mine && !mine.lost) { mine.lost = true; count('noclick'); } }, CLICK_AFTER_UP_MS);
            },

            /** pointercancel — the browser took the gesture (scroll): the cells are not touched. */
            cancel: function (e) {
                if (e.pointerType === 'mouse') {
                    mouse = null;
                    if (h.press) h.press(-1, 'none');
                    return;
                }
                if (fingers > 0) fingers--;
                if (!g || e.pointerId !== g.id) return;
                abandon();
                if (!g.ended) count('scrolled');
                g = null;
            },

            /** click on the board; cell — index of the clicked cell, −1 when none. */
            click: function (e, cell) {
                if (e.detail === 0) {            // keyboard, screen reader, script
                    if (cell >= 0) h.primary(cell, 'key');
                    return true;
                }
                var own = (g && g.ended && e.timeStamp - g.upAt < CLICK_AFTER_UP_MS) ? g : null;
                g = null;
                if (!own) {
                    // No finger gesture ended just now: this is the click of a mouse
                    // release, which has already acted.
                    return false;
                }
                if (own.multi || own.fired) return false;
                if (own.moved) { count('drifted'); return false; }
                if (own.cell < 0) return false;
                if (own.holdable && own.held >= own.at * TAP_SHARE) { count('abandoned'); return false; }
                count('taps');
                if (own.wobble > counts.wobble) { counts.wobble = Math.round(own.wobble * 10) / 10; }
                h.primary(own.cell, 'tap');
                return true;
            },

            /** contextmenu on the board; returns true when the browser menu must not open. */
            contextmenu: function (e, cell) {
                if (g && !g.ended) {            // the system's own long press on a finger
                    if (g.holdable) fireHold();
                    return true;
                }
                if (mouse || (g && g.ended && e.timeStamp - g.upAt < CLICK_AFTER_UP_MS)) return true;
                // The menu of a right click: the press has already flagged. Windows
                // sends it AFTER the button is released, when no gesture is left to
                // recognise it by — hence the button and the clock.
                if (e.button === 2 || e.timeStamp - rightAt < CLICK_AFTER_UP_MS) return true;
                // The menu key on a focused cell.
                if (cell >= 0) h.secondary(cell, 'key');
                return true;
            },

            /** Everything is dropped: the page is hidden, the board was rebuilt. */
            reset: function () {
                abandon();
                g = null;
                mouse = null;
                fingers = 0;
                if (h.press) h.press(-1, 'none');
            },

            state: function () {
                return {
                    finger: g ? { cell: g.cell, moved: g.moved, fired: g.fired, multi: g.multi, ended: g.ended, ring: g.ring } : null,
                    mouse: mouse ? { left: mouse.left, right: mouse.right, middle: mouse.middle, chord: mouse.chord } : null,
                    fingers: fingers
                };
            },

            /** Field counters for the page's telemetry; null when nothing changed. */
            takeCounts: function () {
                if (!dirty) return null;
                dirty = false;
                var out = {};
                for (var k in counts) out[k] = counts[k];
                return out;
            }
        };
    }

    return {
        create: create,
        HOLD_MS: HOLD_MS, RING_DELAY_MS: RING_DELAY_MS, TAP_SHARE: TAP_SHARE, SLOP_PX: SLOP_PX, CLICK_AFTER_UP_MS: CLICK_AFTER_UP_MS
    };
});
